import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { CheckCircle, ArrowRight, Users, FolderOpen, AlertTriangle } from "lucide-react"
import { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next'
import { StripePaymentForm } from "./StripePaymentForm"
import { LogoLoader } from "@/components/ui/logo-loader"

interface SubscriptionUpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPlan: string
  currentPlanBilling?: string  // "monthly" oder "yearly"
  currentUsers?: number        // Aktuelle Benutzeranzahl
  newPlan: string
  newPlanName: string
  price: string
  duration: string
  selectedUsers?: number
  maxUsers?: number
  maxProjects?: number
  features?: string[]
  billing?: 'monthly' | 'yearly'
  isPerSeat?: boolean
  pricePerSeat?: number
  minSeats?: number
  maxSeats?: number | null
  onConfirm: () => void
  onCreatePaymentIntent?: (seatCount: number) => Promise<{ clientSecret: string; paymentIntentId: string } | null>
  onPaymentSuccess?: () => void
  isLoading?: boolean
}

export function SubscriptionUpgradeDialog({
  open,
  onOpenChange,
  currentPlan,
  currentPlanBilling = "monthly",
  currentUsers = 1,
  newPlan,
  newPlanName,
  price,
  duration,
  selectedUsers = 1,
  maxUsers = 1,
  maxProjects = 1,
  features = [],
  billing = 'monthly',
  isPerSeat = false,
  pricePerSeat = 0,
  minSeats = 1,
  maxSeats = null,
  onConfirm,
  onCreatePaymentIntent,
  onPaymentSuccess,
  isLoading = false
}: SubscriptionUpgradeDialogProps) {
  const { t } = useTranslation(['components.SubscriptionUpgradeDialog', 'common'])
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  // Calculate the effective minimum seats (must accommodate current users)
  const effectiveMinSeats = Math.max(minSeats, currentUsers)
  const [seatCount, setSeatCount] = useState(effectiveMinSeats)

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setPaymentIntentId(null)
      setShowPaymentForm(false)
      setIsCreatingPaymentIntent(false)
    } else {
      // Reset seat count when dialog opens - use effective minimum that accommodates current users
      const newEffectiveMin = Math.max(minSeats, currentUsers)
      setSeatCount(newEffectiveMin)
    }
  }, [open, minSeats, currentUsers])

  // Handle payment intent creation (called when user clicks order button)
  const handleCreatePaymentIntent = async () => {
    if (!onCreatePaymentIntent || isCreatingPaymentIntent || clientSecret) {
      return
    }

    setIsCreatingPaymentIntent(true)
    try {
      const result = await onCreatePaymentIntent(seatCount)
      if (result?.clientSecret) {
        setClientSecret(result.clientSecret)
        setPaymentIntentId(result.paymentIntentId)
        setShowPaymentForm(true)
      }
    } catch (error) {
      console.error('❌ Error creating payment intent:', error)
    } finally {
      setIsCreatingPaymentIntent(false)
    }
  }

  const planMap: Record<string, string> = {
    kostenlos: t('components.SubscriptionUpgradeDialog:plans.free'),
    free: t('components.SubscriptionUpgradeDialog:plans.free'),
    expert: t('components.SubscriptionUpgradeDialog:plans.expert'),
    pro: t('components.SubscriptionUpgradeDialog:plans.pro'),
    rise: t('components.SubscriptionUpgradeDialog:plans.rise'),
    enterprise: t('components.SubscriptionUpgradeDialog:plans.enterprise'),
  };

  /** Stripe plan id is often a UUID — use `displayNameFallback` only for that case (target plan), never for current plan. */
  const resolvePlanLabel = (planKey: string, displayNameFallback?: string): string => {
    const mapped = planMap[planKey.toLowerCase()];
    if (mapped) return mapped;
    return displayNameFallback ?? planKey;
  };

  const getFeatureName = (featureKey: string): string => {
    const translationKey = `components.SubscriptionUpgradeDialog:featureNames.${featureKey}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return a formatted version of the key
    if (translated === translationKey) {
      return featureKey.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    return translated;
  }

  const formatPrice = (price: string) => {
    const numPrice = parseFloat(price.replace('€', ''))
    return `€${numPrice.toLocaleString('de-DE')}`
  }

  const translatedNewPlanName = resolvePlanLabel(newPlan, newPlanName);
  const translatedCurrentPlanName = resolvePlanLabel(currentPlan);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('components.SubscriptionUpgradeDialog:title')}</DialogTitle>
          <DialogDescription>
            {t('components.SubscriptionUpgradeDialog:switchingTo', { planName: translatedNewPlanName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Plan Status */}
          <div className="card-container p-4">
            <div className="text-center mb-4">
              <div className="text-xs text-[#666666] mb-2">{t('components.SubscriptionUpgradeDialog:currentPlan')}</div>
              <div className="text-sm font-semibold text-[#333333] mb-1">
                {translatedCurrentPlanName} ({currentPlanBilling === 'yearly' ? t('components.SubscriptionUpgradeDialog:billingYearly') : t('components.SubscriptionUpgradeDialog:billingMonthly')})
              </div>
              <div className="text-xs text-[#666666]">
                {currentUsers} {t('components.SubscriptionUpgradeDialog:activeUsers')}
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-3 py-3 border-t border-[#E5E5E5]">
              <span className="text-sm font-medium text-[#666666]">
                {translatedCurrentPlanName}
              </span>
              <ArrowRight className="w-4 h-4 text-[#999999]" />
              <span className="text-sm font-semibold text-[#4A90E2]">
                {translatedNewPlanName}
              </span>
            </div>
          </div>

          {/* Plan Details Summary */}
          <Card className="card-container">
            <CardContent className="p-4">
              {/* Price Header */}
              <div className="text-center mb-4 pb-4 border-b border-[#E5E5E5]">
                <div className="text-2xl font-semibold text-[#333333] mb-1">
                  {isPerSeat 
                    ? `€${((pricePerSeat * seatCount) / 100).toFixed(0)}` 
                    : formatPrice(price)}
                </div>
                <div className="text-sm text-[#666666]">
                  {isPerSeat && (
                    <div>
                      {`€${(pricePerSeat / 100).toFixed(0)} × ${seatCount} seats ${duration}`}
                    </div>
                  )}
                  {!isPerSeat && duration}
                </div>
              </div>

              {/* Seat Selector for per-seat plans */}
              {isPerSeat && !showPaymentForm && (
                <div className="mb-4 pb-4 border-b border-[#E5E5E5]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-[#333333]">
                      {t('components.SubscriptionUpgradeDialog:numberOfSeats', { defaultValue: 'Number of seats' })}
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSeatCount(Math.max(effectiveMinSeats, seatCount - 1))}
                        disabled={seatCount <= effectiveMinSeats}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5E5E5] rounded bg-white text-[#333333] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        value={seatCount}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || effectiveMinSeats;
                          const clamped = Math.max(effectiveMinSeats, maxSeats ? Math.min(maxSeats, val) : val);
                          setSeatCount(clamped);
                        }}
                        min={effectiveMinSeats}
                        max={maxSeats || undefined}
                        className="w-16 h-8 text-center border border-[#E5E5E5] rounded px-2 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setSeatCount(maxSeats ? Math.min(maxSeats, seatCount + 1) : seatCount + 1)}
                        disabled={maxSeats !== null && seatCount >= maxSeats}
                        className="w-8 h-8 flex items-center justify-center border border-[#E5E5E5] rounded bg-white text-[#333333] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {/* Show warning if current users require more seats than plan minimum */}
                  {currentUsers > minSeats && (
                    <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1.5 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {t('components.SubscriptionUpgradeDialog:currentUsersRequireSeats', { 
                          defaultValue: `You have ${currentUsers} active users. Minimum seats adjusted accordingly.`,
                          count: currentUsers 
                        })}
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-[#666666]">
                    {t('components.SubscriptionUpgradeDialog:seatRange', { 
                      defaultValue: `Min: ${effectiveMinSeats}, Max: ${maxSeats || 'unlimited'}`,
                      min: effectiveMinSeats, 
                      max: maxSeats || 'unlimited' 
                    })}
                  </div>
                </div>
              )}

              {/* Plan Features */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#4A90E2]" />
                    <span className="text-[#333333]">{t('components.SubscriptionUpgradeDialog:users')}</span>
                  </div>
                  <div className="font-medium text-[#333333]">
                    {selectedUsers} / {maxUsers === 999 ? t('components.SubscriptionUpgradeDialog:unlimited') : maxUsers}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-[#4A90E2]" />
                    <span className="text-[#333333]">{t('components.SubscriptionUpgradeDialog:projects')}</span>
                  </div>
                  <div className="font-medium text-[#333333]">
                    {maxProjects === 999 ? t('components.SubscriptionUpgradeDialog:unlimited') : t('components.SubscriptionUpgradeDialog:upTo', { count: maxProjects })}
                  </div>
                </div>

                {features.length > 0 && (
                  <div className="pt-3 border-t border-[#E5E5E5]">
                    <div className="text-xs font-medium text-[#333333] mb-2">{t('components.SubscriptionUpgradeDialog:features')}</div>
                    <div className="space-y-1.5">
                      {features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-[#666666]">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span>{getFeatureName(feature)}</span>
                        </div>
                      ))}
                      {features.length > 3 && (
                        <div className="text-xs text-[#999999] pt-1">
                          {t('components.SubscriptionUpgradeDialog:additionalFeatures', { count: features.length - 3 })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stripe Payment Form */}
          {showPaymentForm && clientSecret ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#333333]">{t('components.SubscriptionUpgradeDialog:paymentMethod')}</h3>
              <StripePaymentForm
                clientSecret={clientSecret}
                paymentIntentId={paymentIntentId || undefined}
                onSuccess={async (intentId: string) => {
                  if (onPaymentSuccess) {
                    await onPaymentSuccess()
                  } else {
                    onConfirm()
                  }
                }}
                onError={(error) => {
                  console.error('❌ Payment error:', error)
                }}
                isLoading={isLoading}
              />
                    </div>
          ) : isCreatingPaymentIntent ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[#333333]">{t('components.SubscriptionUpgradeDialog:paymentMethod')}</h3>
              <div className="flex items-center justify-center p-8">
                <LogoLoader size="md" text={t('common:loading')} showText />
              </div>
          </div>
          ) : (
            <>
              {/* Confirmation */}
              <div className="card-container p-4 border-l-4 border-l-[#4A90E2]">
            <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#4A90E2] mt-0.5 flex-shrink-0" />
              <div>
                    <p className="text-sm font-medium text-[#333333]">
                  {t('components.SubscriptionUpgradeDialog:confirmationTitle', { 
                        planName: translatedNewPlanName,
                    users: selectedUsers > 1 ? t('components.SubscriptionUpgradeDialog:confirmationUsers', { count: selectedUsers }) : '',
                        billing: billing === 'yearly' ? t('components.SubscriptionUpgradeDialog:confirmationBillingYearly') : 
                                 t('components.SubscriptionUpgradeDialog:confirmationBillingMonthly')
                  })}
                </p>
                    <p className="text-xs text-[#666666] mt-1">
                  {t('components.SubscriptionUpgradeDialog:confirmationNote')}
                </p>
              </div>
            </div>
          </div>

              {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
                  disabled={isLoading || isCreatingPaymentIntent}
            >
              {t('components.SubscriptionUpgradeDialog:cancel')}
            </Button>
            <Button
              onClick={() => {
                    if (onCreatePaymentIntent) {
                      handleCreatePaymentIntent()
                    } else {
                      onConfirm()
                    }
              }}
                  className="flex-1"
                  disabled={isLoading || isCreatingPaymentIntent}
            >
                  {isLoading || isCreatingPaymentIntent ? (
                    <LogoLoader size="sm" />
                  ) : (
                    t('components.SubscriptionUpgradeDialog:order')
                  )}
            </Button>
          </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
